/**
 * Types that mirror the Supabase schema (backend/supabase/migrations) and the
 * Worker's JSON responses. All masses are kilograms; the UI converts for display.
 */
export type UnitSystem = 'imperial' | 'metric';
export type Sex = 'male' | 'female';
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'very_active' | 'extra_active';
export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'pre_workout' | 'post_workout' | 'other';
export type MealSource = 'manual' | 'ai_text' | 'ai_image' | 'usda' | 'barcode' | 'recipe';
export type BodyStatSource = 'scale' | 'smart_scale' | 'dexa' | 'bod_pod' | 'bia' | 'calipers' | 'manual' | 'estimate';

export const MEAL_TYPES: readonly MealType[] = ['breakfast', 'lunch', 'dinner', 'snack', 'pre_workout', 'post_workout', 'other'];
export const DIARY_GROUPS: readonly MealType[] = ['breakfast', 'lunch', 'dinner', 'snack', 'pre_workout', 'post_workout', 'other'];
import type { NutrientMap, NutrientSourceMap } from '@engine/nutrients';

export const BODY_STAT_SOURCES: readonly BodyStatSource[] = ['scale', 'smart_scale', 'dexa', 'bod_pod', 'bia', 'calipers', 'manual', 'estimate'];
export const ACTIVITY_LEVELS: readonly ActivityLevel[] = ['sedentary', 'light', 'moderate', 'very_active', 'extra_active'];

export interface Profile {
  id: string;
  display_name: string | null;
  sex: Sex | null;
  birth_date: string | null;
  height_cm: number | null;
  unit_system: UnitSystem;
  timezone: string;
  activity_level: ActivityLevel | null;
  target_calories: number | null;
  target_protein_g: number | null;
  target_carbs_g: number | null;
  target_fat_g: number | null;
  /** First day of the last period. With cycle_length_days, turns on the water model's cycle term. */
  cycle_last_period_start: string | null;
  cycle_length_days: number | null;
  /** Body fat % set in Mighty Peanut Lifting (lifting-owned column, read only here). */
  body_fat_percent: number | null;
}

export type ProfileUpdate = Partial<Omit<Profile, 'id' | 'body_fat_percent'>>;

// ---- Diet settings (public.diet_settings) --------------------------------
/** How the daily burn is worked out. */
export type EnergyMode = 'activity' | 'wearable' | 'adaptive';
export const ENERGY_MODES: readonly EnergyMode[] = ['activity', 'wearable', 'adaptive'];
export type BmrMode = 'mifflin' | 'lean' | 'custom';
export const BMR_MODES: readonly BmrMode[] = ['mifflin', 'lean', 'custom'];

export type MacroMode = 'grams' | 'percent' | 'protein_fat_first';
export type ProteinBasis = 'g' | 'g_per_kg' | 'g_per_lb' | 'g_per_kg_lean' | 'g_per_lb_lean';

/** The macro planner's inputs, kept so the form reopens as it was left. */
export interface MacroPlan {
  mode: MacroMode;
  /** Calorie target typed in percent and protein-and-fat-first modes. */
  calories?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  protein_pct?: number;
  carbs_pct?: number;
  fat_pct?: number;
  protein_amount?: number;
  protein_basis?: ProteinBasis;
  fat_amount?: number;
  fat_basis?: 'g' | 'pct';
}

export type ActivityField =
  | 'active_kcal' | 'total_kcal' | 'steps' | 'distance' | 'exercises' | 'heart_rate'
  | 'resting_hr' | 'hrv' | 'sleep' | 'spo2' | 'respiratory_rate' | 'vo2max';

/** One block of the food diary: a food section, or the watch "Activity & body" panel. */
export interface DiaryBlock {
  id: string;
  kind: 'food' | 'activity';
  label: string;
  /** Shown only on days it has entries. */
  hidden?: boolean;
  /** Activity block only: which numbers to show. */
  fields?: ActivityField[];
}

export interface DietSettings {
  energy_mode: EnergyMode;
  bmr_mode: BmrMode;
  bmr_custom_kcal: number | null;
  /** Wearable mode: add the thermic effect of the day's food to the burn. */
  include_tef: boolean;
  macro_plan: MacroPlan | null;
  /** Empty = the default layout. */
  diary_layout: DiaryBlock[];
  /** Weekly change in % of body weight when the goal has no date (negative = loss). */
  goal_pace_pct: number | null;
  /** TEF page choices (lib/tef.ts reads them). */
  tef_profile: Record<string, string>;
}

export const DEFAULT_SETTINGS: DietSettings = {
  energy_mode: 'adaptive', bmr_mode: 'mifflin', bmr_custom_kcal: null, include_tef: true,
  macro_plan: null, diary_layout: [], goal_pace_pct: null, tef_profile: {},
};

// ---- Wearable days (public.diet_activity_days) ---------------------------
export type ActivitySource = 'health_connect' | 'manual';

/** One app that sent numbers for a day (Health Connect data origin), with its own totals. */
export interface ActivityOrigin {
  package: string;
  name: string | null;
  /** Part of the counted total (Health Connect counts overlapping data once, by app priority). */
  counted: boolean | null;
  active_kcal?: number;
  total_kcal?: number;
  steps?: number;
  distance_m?: number;
  exercise_minutes?: number;
  avg_hr?: number;
  min_hr?: number;
  max_hr?: number;
  sleep_minutes?: number;
}

export interface ActivityExercise {
  title: string | null;
  type: string;
  start: string;
  end: string;
  minutes: number;
  kcal?: number;
}

export interface ActivityDay {
  local_date: string;
  source: ActivitySource;
  /** Energy from movement only (resting energy excluded). */
  active_kcal: number | null;
  /** Resting + active, as a watch shows "total calories". */
  total_kcal: number | null;
  steps: number | null;
  /** Steps taken while no app recorded active calories; the burn prices them as walking. */
  steps_uncovered: number | null;
  distance_m: number | null;
  exercise_minutes: number | null;
  avg_hr: number | null;
  min_hr: number | null;
  max_hr: number | null;
  resting_hr: number | null;
  hrv_rmssd_ms: number | null;
  spo2_pct: number | null;
  respiratory_rate: number | null;
  vo2max: number | null;
  sleep_minutes: number | null;
  exercises: ActivityExercise[];
  origins: ActivityOrigin[];
  day_complete: boolean;
  notes: string | null;
  synced_at: string;
}

/** What the website writes for a day typed in by hand. */
export interface ManualActivityInput {
  local_date: string;
  active_kcal: number | null;
  total_kcal: number | null;
  steps: number | null;
  resting_hr: number | null;
  hrv_rmssd_ms: number | null;
  sleep_minutes: number | null;
  notes: string | null;
}

// ---- Product submissions (public.food_submissions) ------------------------
export interface FoodSubmissionInsert {
  barcode: string | null;
  name: string;
  brand: string | null;
  serving_label: string | null;
  serving_grams: number | null;
  servings_per_container: number | null;
  /** As printed for one serving, by label field name, plus less_than_one. */
  label: Record<string, number | string[]>;
  /** Per 100 g, catalog keys. */
  nutrients: NutrientMap;
  ingredients: string | null;
  allergens: string[];
  ai_model: string | null;
  label_check: string | null;
  food_id: number | null;
  /** 'new' (default) or 'correction' of a product whose data was wrong or incomplete. */
  kind?: SubmissionKind;
  /** Shared food a correction is for. */
  target_food_id?: number | null;
  /** What the app showed before the correction. */
  original?: SubmissionOriginal;
  user_note?: string | null;
  /** Paths in the food-photos bucket, "<user id>/<file>". */
  photo_paths?: string[];
}

/** Fields of one of the user's own foods that a correction may replace. */
export type FoodUpdate = Partial<Pick<FoodInsert, 'name' | 'brand' | 'nutrients' | 'nutrient_sources' | 'portions' | 'notes' | 'barcode'>>;

export type SubmissionKind = 'new' | 'correction';
export type SubmissionStatus = 'pending' | 'approved' | 'rejected';

export interface SubmissionOriginal {
  /** Where the shown values came from: the shared database, the user's own food, or Open Food Facts. */
  source?: 'database' | 'mine' | 'open_food_facts';
  name?: string;
  brand?: string | null;
  serving_label?: string | null;
  serving_grams?: number | null;
  /** Per 100 g. */
  nutrients?: NutrientMap;
}

/** The shared food a submission changes (or created), as the review queue shows it. */
export interface SubmissionTarget {
  id: number;
  source: FoodSource;
  name: string;
  brand: string | null;
  barcode: string | null;
  nutrients: NutrientMap;
  nutrient_sources: NutrientSourceMap;
  portions: Array<{ label: string; grams: number }>;
  notes: string | null;
}

/** One row of admin_food_submissions(). */
export interface AdminSubmission {
  id: number;
  kind: SubmissionKind;
  status: SubmissionStatus;
  created_at: string;
  reviewed_at: string | null;
  submitter_id: string;
  submitter_email: string | null;
  barcode: string | null;
  name: string;
  brand: string | null;
  serving_label: string | null;
  serving_grams: number | null;
  servings_per_container: number | null;
  label: Record<string, unknown>;
  /** Per 100 g. */
  nutrients: NutrientMap;
  ingredients: string | null;
  allergens: string[];
  ai_model: string | null;
  label_check: string | null;
  user_note: string | null;
  reviewer_note: string | null;
  photo_paths: string[];
  original: SubmissionOriginal;
  food_id: number | null;
  target_food_id: number | null;
  promoted_food_id: number | null;
  target: SubmissionTarget | null;
}

/** The owner's final values for an approval; a key left out keeps the submitted value. */
export interface ReviewedFood {
  name?: string;
  brand?: string | null;
  barcode?: string | null;
  serving_label?: string | null;
  serving_grams?: number | null;
  ingredients?: string | null;
  /** Per 100 g. */
  nutrients?: NutrientMap;
}

/** A user's own view of what they sent in. */
export interface MySubmission {
  id: number;
  kind: SubmissionKind;
  status: SubmissionStatus;
  name: string;
  barcode: string | null;
  reviewer_note: string | null;
  created_at: string;
}

// ---- Worker: POST /api/read-label -----------------------------------------
export type { LabelField, LabelPart, PartStatus, ReadLabelResult } from '@engine/label';
export interface ReadLabelPhoto { data: string; mimeType: string }
export interface ReadLabelRequest {
  nutrition?: ReadLabelPhoto;
  front?: ReadLabelPhoto;
  ingredients?: ReadLabelPhoto;
  barcode?: string;
}
export type ReadLabelResponse = import('@engine/label').ReadLabelResult & { requestId: string; model: string | null; provider: string | null };

export interface MealItem {
  name: string;
  /** Set when the item came from the food database. */
  food_id?: number;
  /** Full nutrients for this item's amount (catalog keys), when known. */
  nutrients?: NutrientMap;
  quantity: number;
  unit: string;
  grams: number;
  preparation?: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g?: number;
  sugar_g?: number;
  alcohol_g?: number;
  sodium_mg?: number;
  potassium_mg?: number;
  confidence?: number;
}

export interface MealNutrients {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number | null;
  sugar_g: number | null;
  alcohol_g: number | null;
  sodium_mg: number | null;
  potassium_mg: number | null;
}

export interface MealLog extends MealNutrients {
  id: string;
  /** Full nutrient totals (catalog keys). Empty for meals logged before the food database. */
  nutrients: NutrientMap;
  eaten_at: string;
  local_date: string;
  meal_type: MealType;
  /** Custom diary section (diet_settings.diary_layout id); null = the section of meal_type. */
  diary_section: string | null;
  description: string;
  source: MealSource;
  items: MealItem[];
  ai_model: string | null;
  ai_confidence: number | null;
}

export interface MealInsert extends MealNutrients {
  nutrients: NutrientMap;
  eaten_at: string;
  local_date: string;
  meal_type: MealType;
  diary_section?: string | null;
  description: string;
  source: MealSource;
  items: MealItem[];
  ai_model: string | null;
  ai_confidence: number | null;
}

export interface BodyStat {
  id: number;
  measured_at: string;
  local_date: string;
  weight_kg: number;
  body_fat_pct: number | null;
  lean_mass_kg: number | null;
  source: BodyStatSource;
  is_fasted: boolean | null;
  notes: string | null;
}

export interface BodyStatInsert {
  measured_at: string;
  local_date: string;
  weight_kg: number;
  body_fat_pct: number | null;
  lean_mass_kg: number | null;
  source: BodyStatSource;
  is_fasted: boolean | null;
  notes: string | null;
}

export interface BodyGoal {
  id: number;
  target_weight_kg: number | null;
  target_lean_mass_kg: number | null;
  target_body_fat_pct: number | null;
  target_date: string | null;
  planned_daily_calories: number | null;
  is_active: boolean;
  notes: string | null;
}

export type BodyGoalInput = Omit<BodyGoal, 'id' | 'is_active'>;

export interface DailyMetric {
  day: string;
  meal_count: number;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  alcohol_g: number;
  sodium_mg: number;
  potassium_mg: number;
  weigh_in_count: number;
  weight_kg: number | null;
  body_fat_pct: number | null;
  lean_mass_kg: number | null;
}

// ---- Worker: POST /api/analyze-meal --------------------------------------
export interface AnalyzeMealRequest {
  text?: string;
  image?: { data: string; mimeType?: string };
  mealType?: MealType;
}

export interface MealDraft extends MealNutrients {
  description: string;
  meal_type: MealType;
  source: 'ai_text' | 'ai_image' | 'manual';
  items: MealItem[];
  ai_model: string | null;
  ai_confidence: number | null;
}

export interface AnalyzeMealResponse {
  requestId: string;
  isFood: boolean;
  meal: MealDraft | null;
  assumptions: string[];
  clarifyingQuestion: string | null;
  warnings: string[];
  usage?: { latencyMs?: number; model?: string; provider?: string };
}

// ---- Food database --------------------------------------------------------
export type { NutrientMap, NutrientSourceMap, NutrientSource, NutrientDef } from '@engine/nutrients';

export type FoodSource = 'usda_foundation' | 'usda_sr_legacy' | 'usda_branded' | 'usda_survey' | 'community' | 'user' | 'ai' | 'recipe' | 'barcode';

export interface FoodPortion { label: string; grams: number }

export interface FoodSearchHit {
  id: number;
  source: FoodSource;
  fdc_id: number | null;
  name: string;
  brand: string | null;
  category: string | null;
  energy_kcal: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  nutrient_count: number;
  is_mine: boolean;
}

export interface Food {
  id: number;
  owner_id: string | null;
  source: FoodSource;
  fdc_id: number | null;
  name: string;
  brand: string | null;
  category: string | null;
  /** Per 100 g. Missing key = unknown. */
  nutrients: NutrientMap;
  nutrient_sources: NutrientSourceMap;
  portions: FoodPortion[];
  notes: string | null;
  ai_model: string | null;
  updated_at: string;
  /** GTIN of a scanned product, or of a shared USDA branded product (20260924000000_diet_food_barcodes.sql). */
  barcode?: string | null;
}

export interface FoodAiFill {
  food_id: number;
  nutrients: NutrientMap;
  model: string | null;
  confidence: number | null;
  notes: string[];
}

export interface FoodWithFill { food: Food; fill: FoodAiFill | null }

export interface FoodInsert {
  source: 'user' | 'ai' | 'recipe' | 'barcode';
  name: string;
  brand: string | null;
  category: string | null;
  nutrients: NutrientMap;
  nutrient_sources: NutrientSourceMap;
  portions: FoodPortion[];
  notes: string | null;
  ai_model: string | null;
  barcode?: string | null;
}

export type { BarcodeFound, BarcodeLookup, BarcodeNotFound, OffProductSummary } from '@engine/barcode';
/** What GET /api/barcode/:code returns. */
export type BarcodeLookupResponse = import('@engine/barcode').BarcodeLookup & { cached?: boolean; requestId?: string };

export interface CompleteNutrientsRequest {
  food: { name: string; category?: string; brand?: string; details?: string };
  known: NutrientMap;
  want?: string[];
}

export interface CompleteNutrientsResponse {
  requestId: string;
  isFood: boolean;
  values: NutrientMap;
  confidence: Record<string, number>;
  overallConfidence: number | null;
  unanswered: string[];
  assumptions: string[];
  warnings: string[];
  usage?: { model?: string; provider?: string };
  cached: boolean;
}

// ---- Worker: POST /api/compute-trajectory --------------------------------
export type { TrajectoryResult, TrajectoryPoint, DayInput, TdeeStatus } from '@engine/trajectory';
